/**
 * Worked examples of mental maps. Shown before people draw, so nobody starts
 * from a blank canvas wondering what a mental map is; each can also be loaded
 * as a starting point and reworked.
 */

import type { MapDoc } from './model';

export interface MapExample {
  id: string;
  title: string;
  /** The question this kind of map answers. */
  prompt: string;
  /** How to read it, in one sentence. */
  reading: string;
  doc: MapDoc;
}

export const MAP_EXAMPLES: MapExample[] = [
  {
    id: 'layers',
    title: 'Layers',
    prompt: 'What sits on top of what?',
    reading:
      'Read it from the bottom up. Each layer rests on the one below it: without the foundation there is no platform, and without the platform there are no tools.',
    doc: {
      shapes: [
        { id: 'l3', kind: 'layer', label: 'Tools', tone: 'priority', size: 'l', x: 260, y: 230 },
        { id: 'l2', kind: 'layer', label: 'Platform', tone: 'neutral', size: 'l', x: 260, y: 340 },
        { id: 'l1', kind: 'layer', label: 'Foundation', tone: 'working', size: 'l', x: 260, y: 450 },
      ],
      links: [],
    },
  },
  {
    id: 'two-kinds-of-work',
    title: 'Two kinds of work',
    prompt: 'What are the big stretches of work, and what happens in each?',
    reading:
      'The arrows across the top are the phases, left to right. Under each one is the work that belongs to it.',
    doc: {
      shapes: [
        { id: 'p1', kind: 'phase', label: 'Getting to a set of priorities', tone: 'priority', size: 'xl', x: 150, y: 180 },
        { id: 'p2', kind: 'phase', label: 'Keeping everyone aligned to them', tone: 'working', size: 'xl', x: 600, y: 180 },
        { id: 'a1', kind: 'rounded', label: 'Gather the options', tone: 'neutral', size: 'm', x: 150, y: 380 },
        { id: 'a2', kind: 'rounded', label: 'Rank them together', tone: 'neutral', size: 'm', x: 300, y: 380 },
        { id: 'a3', kind: 'diamond', label: 'Where is the cut?', tone: 'tension', size: 'm', x: 450, y: 380 },
        { id: 'b1', kind: 'rounded', label: 'Tell everyone', tone: 'neutral', size: 'm', x: 620, y: 380 },
        { id: 'b2', kind: 'rounded', label: 'Check the work matches', tone: 'neutral', size: 'm', x: 770, y: 380 },
        { id: 'b3', kind: 'rounded', label: 'Revisit when things change', tone: 'neutral', size: 'm', x: 920, y: 380 },
      ],
      links: [
        { id: 'k1', from: 'a1', to: 'a2', kind: 'leads_to' },
        { id: 'k2', from: 'a2', to: 'a3', kind: 'leads_to' },
        { id: 'k3', from: 'b1', to: 'b2', kind: 'leads_to' },
        { id: 'k4', from: 'b2', to: 'b3', kind: 'leads_to' },
      ],
    },
  },
  {
    id: 'what-causes-what',
    title: 'What causes what',
    prompt: 'How do you see this problem?',
    reading:
      'Follow the arrows: two causes feed one problem, and one decision is the way out. Coral marks what hurts; mustard marks what matters most.',
    doc: {
      shapes: [
        { id: 'a', kind: 'rectangle', label: 'Too many projects', tone: 'neutral', size: 'm', x: 120, y: 120 },
        { id: 'b', kind: 'rectangle', label: 'No shared ranking', tone: 'neutral', size: 'm', x: 120, y: 420 },
        { id: 'c', kind: 'circle', label: 'Everyone is stretched', tone: 'tension', size: 'l', x: 440, y: 230 },
        { id: 'd', kind: 'diamond', label: 'What do we stop?', tone: 'priority', size: 'm', x: 800, y: 270 },
        { id: 'e', kind: 'rounded', label: 'Finish three things', tone: 'working', size: 'm', x: 1020, y: 270 },
      ],
      links: [
        { id: 'l1', from: 'a', to: 'c', kind: 'leads_to' },
        { id: 'l2', from: 'b', to: 'c', kind: 'leads_to' },
        { id: 'l3', from: 'c', to: 'd', kind: 'leads_to' },
        { id: 'l4', from: 'd', to: 'e', kind: 'supports' },
      ],
    },
  },
  {
    id: 'core-and-edge',
    title: 'Core and edge',
    prompt: 'What is at the centre, and what is at the edge?',
    reading:
      'Rings are boundaries. The point in the middle is the focus; whatever sits outside the outer ring is out of scope.',
    doc: {
      shapes: [
        { id: 'r1', kind: 'ring', label: 'What we touch', tone: 'neutral', size: 'xl', x: 400, y: 200 },
        { id: 'r2', kind: 'ring', label: 'What we own', tone: 'working', size: 'l', x: 470, y: 270 },
        { id: 'p', kind: 'point', label: 'Why we exist', tone: 'priority', size: 's', x: 534, y: 334 },
        { id: 'o1', kind: 'hexagon', label: 'Things we cannot do yet', tone: 'tension', size: 'm', x: 860, y: 140 },
        { id: 'o2', kind: 'rectangle', label: 'Out of scope', tone: 'neutral', size: 'm', x: 140, y: 520 },
      ],
      links: [{ id: 'l1', from: 'o1', to: 'r1', kind: 'conflicts' }],
    },
  },
  {
    id: 'what-rests-on-what',
    title: 'What rests on what',
    prompt: 'What has to be true underneath for the top to hold?',
    reading:
      'Read it bottom to top. Each lower shape supports the one above it; the triangle is where it is all heading.',
    doc: {
      shapes: [
        { id: 't', kind: 'triangle', label: 'The goal', tone: 'priority', size: 'l', x: 500, y: 40 },
        { id: 'm1', kind: 'rounded', label: 'Plan people trust', tone: 'neutral', size: 'm', x: 380, y: 330 },
        { id: 'm2', kind: 'rounded', label: 'Time to do it', tone: 'tension', size: 'm', x: 700, y: 330 },
        { id: 'b1', kind: 'rectangle', label: 'Honest numbers', tone: 'working', size: 'm', x: 260, y: 580 },
        { id: 'b2', kind: 'rectangle', label: 'Agreed priorities', tone: 'neutral', size: 'm', x: 540, y: 580 },
        { id: 'b3', kind: 'hexagon', label: 'Enough people', tone: 'tension', size: 'm', x: 820, y: 580 },
      ],
      links: [
        { id: 'l1', from: 'm1', to: 't', kind: 'supports' },
        { id: 'l2', from: 'm2', to: 't', kind: 'supports' },
        { id: 'l3', from: 'b1', to: 'm1', kind: 'supports' },
        { id: 'l4', from: 'b2', to: 'm1', kind: 'supports' },
        { id: 'l5', from: 'b3', to: 'm2', kind: 'supports' },
      ],
    },
  },
  {
    id: 'two-worlds',
    title: 'Two worlds that overlap',
    prompt: 'Where do two groups want the same thing, and where do they not?',
    reading:
      'Each circle is one group. What sits in the overlap is shared; the dashed coral line is where they pull against each other.',
    doc: {
      shapes: [
        { id: 'c1', kind: 'ring', label: 'Sales', tone: 'neutral', size: 'xl', x: 250, y: 220 },
        { id: 'c2', kind: 'ring', label: 'Delivery', tone: 'neutral', size: 'xl', x: 480, y: 220 },
        { id: 's', kind: 'point', label: 'Happy customers', tone: 'priority', size: 's', x: 500, y: 350 },
        { id: 'x1', kind: 'rounded', label: 'Say yes fast', tone: 'tension', size: 's', x: 300, y: 300 },
        { id: 'x2', kind: 'rounded', label: 'Protect the team', tone: 'tension', size: 's', x: 700, y: 300 },
      ],
      links: [{ id: 'l1', from: 'x1', to: 'x2', kind: 'conflicts' }],
    },
  },
];
